import { sanitize } from "@/utils/formatUtils";
import { DEFAULT_LOCALE } from "../../../app/i18n";
import { ProductFinancialSector } from "../../product/enums";
import { CommissionsSettingsLevelRule } from "../level/types";
import { CommissionsSettingsRankBase } from "../ranks/types";
import { CommissionsSettings, CommissionsSettingsAgentProfile, CommissionsSettingsRule } from "./types";

const compareStrings = (strA?: string, strB?: string) => {
  if (!strA && !strB) return 0;
  if (!strA) return 1;
  if (!strB) return -1;

  return strA.localeCompare(strB, DEFAULT_LOCALE, { sensitivity: "accent" });
};

export const sortCommissionsSettingsRules = <T extends CommissionsSettingsRule | CommissionsSettingsLevelRule>(
  rules: T[],
): T[] => {
  const sectorOrder = Object.keys(ProductFinancialSector);

  return [...rules].sort((a, b) => {
    const sectorA = sectorOrder.indexOf(a.sector);
    const sectorB = sectorOrder.indexOf(b.sector);

    if (sectorA !== sectorB) {
      return sectorA - sectorB;
    }

    const productGroupCompare = compareStrings(a.productGroup?.name, b.productGroup?.name);
    if (productGroupCompare !== 0) return productGroupCompare;

    const institutionCompare = compareStrings(a.institution?.name, b.institution?.name);
    if (institutionCompare !== 0) return institutionCompare;

    return compareStrings(a.product?.name, b.product?.name);
  });
};

enum DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME {
  SILVER = "silver",
  GOLD = "gold",
  PLATINUM = "platina",
  MANAGER = "manazer",
  TOP_MANAGER = "top manazer",
  STRUCTURE_DIRECTOR = "strukturny riaditel",
  ROS = "riaditel obchodnej siete",
  AMBASADOR = "ambasador",
  COMPANY_PRESIDENT = "prezident spolocnosti",
}

export const getCommissionsSettingsRankColor = (rank?: CommissionsSettingsRankBase) => {
  if (!rank) {
    return "#fff";
  }

  switch (sanitize(rank.name)) {
    case DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME.SILVER:
      return "#e8e8e8";
    case DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME.GOLD:
      return "#ffce2b";
    case DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME.PLATINUM:
      return "#C0C0C0";
    case DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME.MANAGER:
      return "#ffae46";
    case DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME.TOP_MANAGER:
      return "#ffc081";
    case DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME.STRUCTURE_DIRECTOR:
      return "#ef8bac";
    case DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME.ROS:
      return "#cf8ff3";
    case DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME.AMBASADOR:
      return "#c357fa";
    case DEFAULT_COMMISSIONS_SETTINGS_RANK_NAME.COMPANY_PRESIDENT:
      return "#FFED29";
    default:
      return "#fff";
  }
};

export const isCommissionsSettings = (
  settings: CommissionsSettings | CommissionsSettingsAgentProfile,
): settings is CommissionsSettings => {
  return "createdAt" in settings && "updatedAt" in settings && "rules" in settings && "status" in settings;
};
